import Button from 'tio-reimagined/components/button';
import { hash } from '@ember/helper';
import { dropTask } from 'ember-concurrency';
import { eq, not } from 'tio-ui/utilities';
import { fn } from '@ember/helper';
import Modal from 'tio-reimagined/components/modal';
import Footer from 'tio-reimagined/components/footer';
import { Input, RadioGroup } from 'tio-ui/components/forms';
import { LinkTo } from '@ember/routing';
import { on } from '@ember/modifier';
import { PhLink, PhLinkBreak } from 'ember-phosphor-icons';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import { VStack, Section } from 'tio-ui/components/layout';
import Component from '@glimmer/component';
import type { IntlService } from 'ember-intl';
import type { NotificationsService } from 'tio-ui/components/notifications';
import type PersonModel from 'tio-common/models/person';
import type RouterService from '@ember/routing/router-service';

export interface AlertNoLinkedLoansSignature {
  Args: {
    person: PersonModel;
    product?: string;
    didSave?: (person: PersonModel) => void;
  };
}

export default class AlertNoLinkedLoansComponent extends Component<AlertNoLinkedLoansSignature> {
  @service declare intl: IntlService;
  @service declare router: RouterService;
  @service declare notifications: NotificationsService;

  @tracked reason = '';
  @tracked explanation = '';
  @tracked isModalOpen = false;

  get canSubmit() {
    return !!this.reason && (this.reason !== 'other' || !!this.explanation);
  }

  submit = () => {
    this.submitTask.perform();
  };

  submitTask = dropTask(async () => {
    if (!this.canSubmit) {
      return;
    }
    try {
      const saved = await this.saveFormValues.perform();
      this.args.didSave?.(saved);
      this.notifications.add(this.intl.t('common.success'), {
        appearance: 'success',
      });
    } catch (e) {
      console.error(e);
    } finally {
      this.onClose();
    }
  });

  saveFormValues = dropTask(async () => {
    const person = this.args.person;
    const existingPreferences = person.preferences;

    let updatedPreferences: Record<string, unknown> = {
      ...existingPreferences,
      'reason-for-not-linking-loans': this.reason,
    };

    if (this.reason === 'other') {
      updatedPreferences = {
        ...updatedPreferences,
        'explanation-for-not-linking-loans': this.explanation,
      };
    }
    person.preferences = updatedPreferences;

    await person.save();
    return person;
  });

  get observabilityUploadLinkQuery(): string | undefined {
    return this.args.product || '';
  }

  get hasAnsweredWhyNoLoans() {
    return !this.args.person.needsToAnswerWhyNoLinkedLoans;
  }

  onClose = () => {
    this.isModalOpen = false;
    this.reason = '';
    this.explanation = '';
  };

  updateReason = (value: string) => {
    this.reason = value;
    if (value !== 'other') {
      this.explanation = '';
    }
  };

  <template>
    <Section>
      <:body>
        <VStack class="flex flex-col items-center py-6!">
          {{#if this.hasAnsweredWhyNoLoans}}
            <PhLinkBreak @size="32" />
            <p class="w-1/2 text-center">{{t "no_linked_accounts_alert.already_answered"}}</p>
            <div
              class="p-2 border border-gray-200 rounded border-ocean-600 text-ocean-600 w-fit font-semibold"
            >
              <LinkTo
                @route="authenticated.observability.upload"
                @query={{hash source=this.observabilityUploadLinkQuery}}
              >
                {{t "common.accounts.add_account"}}
              </LinkTo>
            </div>
          {{else}}
            <PhLink @size="32" />
            <p>{{t "no_linked_accounts_alert.add_loan_account"}}</p>
            <div
              class="p-2 border border-gray-200 rounded border-ocean-600 text-ocean-600 w-fit font-semibold"
            >
              <LinkTo
                @route="authenticated.observability.upload"
                @query={{hash source=this.observabilityUploadLinkQuery}}
              >
                {{t "common.accounts.add_account"}}
              </LinkTo>
            </div>
            <Button
              @intent="default"
              @appearance="minimal"
              class="flex"
              {{on "click" (fn (mut this.isModalOpen) true)}}
            >
              {{t "no_linked_accounts_alert.not_adding_account"}}
            </Button>
          {{/if}}
        </VStack>
      </:body>
    </Section>

    <Modal @isOpen={{this.isModalOpen}} @onClose={{this.onClose}} class="min-h-64">
      <:header>{{t "no_linked_accounts_alert.heading"}}</:header>
      <:body>
        <form class="flex flex-col gap-y-4">
          <p>{{t "no_linked_accounts_alert.please_tell_us_why"}}</p>
          <RadioGroup @name="noLinkedLoansReason" as |Radio|>
            <Radio
              @label={{t "no_linked_accounts_alert.dont_want_add"}}
              @value="dont_want_add"
              @onChange={{fn this.updateReason "dont_want_add"}}
            />
            <Radio
              @label={{t "no_linked_accounts_alert.unable_add"}}
              @value="unable_add"
              @onChange={{fn this.updateReason "unable_add"}}
            />
            <Radio
              @label={{t "common.other"}}
              @value="other"
              @onChange={{fn this.updateReason "other"}}
            />
          </RadioGroup>
          {{#if (eq this.reason "other")}}
            <Input
              placeholder={{t "no_linked_accounts_alert.please_specify"}}
              @value={{this.explanation}}
              @onInput={{fn (mut this.explanation)}}
            />
          {{/if}}
        </form>
      </:body>
      <:footer>
        <Footer
          @isRunning={{this.submitTask.isRunning}}
          @onSubmit={{this.submit}}
          @submitDisabled={{not this.canSubmit}}
          @onCancel={{this.onClose}}
        />
      </:footer>
    </Modal>
  </template>
}
